import React, { useEffect, useState } from "react";
import axios from "axios";
import {
	FiMessageCircle,
	FiSmartphone,
	FiUsers,
	FiActivity,
	FiCalendar,
} from "react-icons/fi";
import BRANDING from "../config/branding";
import { API_URL } from '../config/api';

const DashboardPage = () => {
	const [chartData, setChartData] = useState(false);
	const [loading, setLoading] = useState(true);
	const [queuesTotal, setQueuesTotal] = useState(0);
	const [messagesTotal, setMessagesTotal] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [isFileProcessing, setIsFileProcessing] = useState(false);
	const [statusSummary, setStatusSummary] = useState({});
	const [startDate, setStartDate] = useState(
		new Date().toISOString().split("T")[0]
	);
	const [endDate, setEndDate] = useState(
		new Date().toISOString().split("T")[0]
	);

	const token = localStorage.getItem("token");

	const fetchData = async (start, end) => {
		setLoading(true);
		try {
		const token = localStorage.getItem("token");

			const res = await axios.get(
				`${API_URL}/checkStats.php?date=${
					start || new Date().toISOString().split("T")[0]
				}`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (res.data?.success) {
				const stats = res.data.stats || [];

				// Only the first row for total queues & uploaded_files
				const firstRow = stats[0] || {};
				const queuesTotal = parseInt(firstRow.total_queues || 0, 10);
				const totalUploaded = parseInt(firstRow.total_uploaded || 0, 10);

				// Calculate total messages (sum of dlr_total)
				const messagesTotal = stats.reduce(
					(sum, item) => sum + parseInt(item.dlr_total || 0, 10),
					0
				);

				// All possible statuses
				const possibleStatuses = ["DELIVRD", "UNDELIV", "EXPIRD", "REJECTD"];

				// Group by network + status
				const statusSummary = {};
				stats.forEach((item) => {
					const network = item.network;
					const status = item.status.toUpperCase();
					const total = parseInt(item.dlr_total || 0, 10);

					if (!statusSummary[network]) {
						// Initialize all statuses to 0
						statusSummary[network] = { total: 0 };
						possibleStatuses.forEach(
							(s) => (statusSummary[network][s.toLowerCase()] = 0)
						);
					}
					statusSummary[network][status.toLowerCase()] = total;
					statusSummary[network].total += total;
				});

				setChartData(true);
				setQueuesTotal(queuesTotal);
				setMessagesTotal(totalUploaded);
				setTotalPages(totalUploaded);
				setIsFileProcessing(false);
				setStatusSummary(statusSummary);
			} else {
				setChartData(false);
			}
		} catch (error) {
			console.error("Failed to fetch messages data", error);
			setChartData(false);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		let interval;

		if (startDate && endDate) {
			fetchData(startDate, endDate);

			interval = setInterval(() => {
				fetchData(startDate, endDate);
			}, 60000);
		}

		return () => clearInterval(interval);
	}, [startDate, endDate]);

	return (
		<div className='flex min-h-screen bg-gray-50'>
			<main className='flex-1 p-10'>
				{/* Header */}
				<div className='text-center mb-12'>
					<h1
    className='text-5xl font-extrabold text-center'
    style={{ color: BRANDING.primaryColor }}
>
    {BRANDING.shortName} SMS Dashboard
</h1>
					<p className='text-gray-600 mt-3 text-lg'>
						Monitor customer communication delivery & performance
					</p>
					<div
    className='w-24 h-1 mx-auto mt-4 rounded-full'
    style={{ backgroundColor: BRANDING.primaryColor }}
></div>
				</div>

				{/* Date Range Picker */}
				<div className='flex flex-col md:flex-row justify-center items-center gap-4 mb-12'>
					<div className='flex items-center gap-2'>
						<FiCalendar style={{ color: BRANDING.primaryColor }} />
						<input
							type='date'
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
							className='border border-gray-300 rounded-full px-4 py-2 shadow focus:outline-none focus:ring-2 focus:ring-[#D42E11]'
						/>
					</div>
					<span className='text-gray-600'>to</span>
					<div className='flex items-center gap-2'>
						<FiCalendar style={{ color: BRANDING.primaryColor }} />
						<input
							type='date'
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
							className='border border-gray-300 rounded-full px-4 py-2 shadow focus:outline-none focus:ring-2 focus:ring-[#D42E11]'
						/>
					</div>
				</div>

				{loading ? (
					<p className='text-center text-[#D42E11] animate-pulse'>
						Loading message data...
					</p>
				) : chartData ? (
					<>
						{/* File Upload Notice */}
						{isFileProcessing && (
							<div className='mb-6 bg-[#D42E11]/10 border border-[#D42E11] text-[#D42E11] px-4 py-3 rounded-lg shadow text-center animate-pulse'>
								<span className='font-semibold'>Processing File...</span> Please
								wait while we process your upload.
							</div>
						)}

						{/* Summary Stats */}
						<div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-14'>
							{[
								{
									icon: <FiMessageCircle />,
									label: "Total Messages",
									value: messagesTotal,
									color: BRANDING.primaryColor,
								},
								{
									icon: <FiMessageCircle />,
									label: "Total SMS Pages",
									value: totalPages,
									color: "#06B6D4",
								},
								{
									icon: <FiSmartphone />,
									label: "Networks",
									value: 4,
									color: "#F59E0B",
								},
								{
									icon: <FiUsers />,
									label: "Pending Items",
									value: queuesTotal,
									color: "#F97316",
								},
								{
									icon: <FiActivity />,
									label: "Date Range",
									value: `${new Date(startDate).toDateString()} - ${new Date(
										endDate
									).toDateString()}`,
									color: "#22C55E",
								},
							].map((item, idx) => (
								<div
									key={idx}
									  className='bg-white p-6 rounded-xl shadow-md border-t-4 flex flex-col items-center justify-center space-y-3 transform hover:scale-105 transition'
    									style={{ borderTopColor: item.color }}
								>
									<div
										className='p-3 rounded-full text-2xl shadow-sm'
										style={{
											backgroundColor: `${item.color}15`,
											color: item.color,
										}}
									>
										{item.icon}
									</div>
									<p className='text-sm text-gray-500'>{item.label}</p>
									<h2 className='text-2xl font-bold text-gray-800'>{item.value}</h2>
								</div>
							))}
						</div>

						{/* Delivery Status Breakdown */}
						<div>
							<h2
    className='text-3xl font-bold mb-8 text-center pb-3'
    style={{
        color: BRANDING.primaryColor,
        borderBottom: `2px solid ${BRANDING.primaryColor}40`,
    }}
>
								Delivery Status by Network
							</h2>
							<div className='grid grid-cols-1 md:grid-cols-2 gap-10'>
								{Object.entries(statusSummary).map(([network, statuses]) => {
									const total = statuses.total || 0;
									const getPercent = (val) =>
										total ? `${Math.round((val / total) * 100)}%` : "0%";

									const statusColors = {
										delivered: "from-green-500 to-green-700",
										pending: "from-yellow-400 to-yellow-600",
										expired: "from-gray-400 to-gray-600",
										undelivered: "from-red-500 to-red-700",
										rejected: "from-red-400 to-red-600",
										unknown: "from-gray-300 to-gray-500",
									};

									return (
										<div
											key={network}
											className='rounded-xl shadow-md p-6 hover:shadow-xl transition duration-300'
											style={{
    border: `1px solid ${BRANDING.primaryColor}30`,
}}
										>
											<h3
    className='text-xl font-semibold mb-4'
    style={{ color: BRANDING.primaryColor }}
>
												{network}
											</h3>
											<p className='text-sm text-gray-600 mb-6'>
												Total:{" "}
												<span
    className='font-medium'
    style={{ color: BRANDING.primaryColor }}
>
													{total}
												</span>{" "}
												messages
											</p>

											<div className='space-y-4'>
												{Object.entries(statuses)
													.filter(([key]) => key !== "total")
													.map(([status, count]) => {
														const percent = getPercent(count);
														return (
															<div key={status}>
																<div className='flex justify-between text-sm text-gray-600 mb-1'>
																	<span className='capitalize'>{status}</span>
																	<span
    className='font-semibold'
    style={{ color: BRANDING.primaryColor }}
>
																		{count} ({percent})
																	</span>
																</div>
																<div className='relative w-full bg-gray-200 h-3 rounded-full overflow-hidden shadow-inner'>
																	<div
																		className={`bg-gradient-to-r ${
																			statusColors[status] ||
																			"from-gray-300 to-gray-500"
																		} h-3 rounded-full transition-all duration-500`}
																		style={{ width: percent }}
																	></div>
																</div>
															</div>
														);
													})}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					</>
				) : (
					<p
    className='text-center'
    style={{ color: BRANDING.primaryColor }}
>
						No messages found for {new Date(startDate).toDateString()} to{" "}
						{new Date(endDate).toDateString()}.
					</p>
				)}
			</main>
		</div>
	);
};

export default DashboardPage;

//#D42E11

// #FFFFFF

//created_at
//batch_id
// msg_cat
// cost_cntr
// total_count
// total_distinct
// uploaded_by
// approved_by
// rawMessage
